import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

type ContentItem =
  | { type: "text"; content: string }
  | { type: "bullets"; items: string[] };

type Section = {
  title: string;
  content: ContentItem[];
};

const sections: Section[] = [
  {
    title: "1. General Information",
    content: [
      { type: "text", content: 'This Refund Policy explains the conditions under which Vantory Labs LTD ("Company", "we", "our", or "us") may issue refunds for payments made through this website (the "Site") or related services.' },
      { type: "text", content: "Registered Address\nVantory Labs LTD\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Contact Information\nEmail: support@vantorylabs.com\nPhone: +447451257361" },
      { type: "text", content: "We aim to provide fair customer support and reasonable refund consideration in accordance with this Policy and applicable consumer protection laws." },
    ],
  },
  {
    title: "2. Refund Eligibility",
    content: [
      { type: "text", content: "Refund requests may be considered under the following circumstances:" },
      {
        type: "bullets",
        items: [
          "A payment was successfully processed directly by the Company or an authorized payment provider",
          "A technical issue or service-related problem substantially affected the availability or functionality of the service",
          "The request is submitted within 14 days from the original transaction date",
          "Sufficient information is provided to verify the purchase and the reported issue",
        ],
      },
      { type: "text", content: "All refund requests are reviewed individually and refunds are not guaranteed." },
    ],
  },
  {
    title: "3. Non-Refundable Situations",
    content: [
      { type: "text", content: "Refunds will generally not be provided in the following cases:" },
      {
        type: "bullets",
        items: [
          "Change of mind after purchase",
          "Failure to use the service",
          "User misunderstanding of product features or functionality",
          "Issues caused by user devices, internet connectivity, or third-party services",
          "Requests submitted outside the applicable refund period",
          "Violations of our Terms of Use",
        ],
      },
      { type: "text", content: "We reserve the right to deny refund requests that do not meet the conditions outlined in this Policy." },
    ],
  },
  {
    title: "4. Subscription Services",
    content: [
      { type: "text", content: "If the Company offers subscription-based services:" },
      {
        type: "bullets",
        items: [
          "Subscriptions may renew automatically unless canceled before the renewal date",
          "Canceling a subscription prevents future billing but does not automatically result in a refund for previous charges",
          "Users remain responsible for charges incurred before cancellation becomes effective",
        ],
      },
      { type: "text", content: "For subscriptions purchased through third-party platforms such as Apple App Store, refunds may be subject to the policies of the applicable platform provider." },
    ],
  },
  {
    title: "5. Refund Request Procedure",
    content: [
      { type: "text", content: "To request a refund, please contact our support team using the contact information below:" },
      { type: "text", content: "Email: support@vantorylabs.com\nPhone: +447451257361" },
      { type: "text", content: "Your request should include:" },
      {
        type: "bullets",
        items: [
          "Full name",
          "Contact information",
          "Proof of payment or transaction details",
          "Description of the issue or reason for the request",
        ],
      },
      { type: "text", content: "We may request additional information where reasonably necessary to investigate the claim." },
    ],
  },
  {
    title: "6. Review Process",
    content: [
      { type: "text", content: "All refund requests are evaluated on a case-by-case basis." },
      { type: "text", content: "The Company reserves the right to:" },
      {
        type: "bullets",
        items: [
          "Approve or deny refund requests",
          "Request supporting documentation or additional verification",
          "Determine whether the request qualifies under this Policy",
        ],
      },
      { type: "text", content: "Our decision will be based on the circumstances of the request, available evidence, and applicable legal obligations." },
    ],
  },
  {
    title: "7. Processing Approved Refunds",
    content: [
      { type: "text", content: "If a refund is approved:" },
      {
        type: "bullets",
        items: [
          "Refunds will generally be issued to the original payment method used for the transaction",
          "Processing times may vary depending on banks, payment processors, or third-party providers",
          "Typical processing time is between 5–14 business days",
        ],
      },
      { type: "text", content: "In certain cases, taxes or processing fees may not be refundable unless required by applicable law." },
    ],
  },
  {
    title: "8. Chargebacks and Payment Disputes",
    content: [
      { type: "text", content: "We encourage users to contact us directly before initiating chargebacks or payment disputes through financial institutions or payment providers." },
      { type: "text", content: "Submitting fraudulent or abusive refund claims may result in suspension or restriction of access to our services." },
    ],
  },
  {
    title: "9. Limitation of Liability",
    content: [
      { type: "text", content: "To the maximum extent permitted by law, Vantory Labs LTD shall not be responsible for:" },
      {
        type: "bullets",
        items: [
          "Service interruptions caused by third-party providers",
          "Device compatibility issues",
          "Internet or connectivity problems outside our control",
          "Delays caused by banks or payment processors",
        ],
      },
      { type: "text", content: "Nothing in this Policy limits rights that cannot legally be excluded under applicable law." },
    ],
  },
  {
    title: "10. Changes to this Refund Policy",
    content: [
      { type: "text", content: "We may update or modify this Refund Policy from time to time." },
      { type: "text", content: 'Updated versions will be published on this page with a revised "Last updated" date.' },
      { type: "text", content: "Continued use of the Site or Services after changes become effective constitutes acceptance of the updated Policy." },
    ],
  },
  {
    title: "11. Contact Information",
    content: [
      { type: "text", content: "Vantory Labs LTD\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Email: support@vantorylabs.com\nPhone: +447451257361" },
    ],
  },
];

export default function RefundPage() {
  const heroRef = useReveal<HTMLDivElement>();

  return (
    <SiteLayout navbarVariant="solid">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              Legal
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Refund <span className="italic font-light">Policy</span>.
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-cream py-16 md:py-24">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="max-w-3xl" data-reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-ink/50 hover:text-ink transition-colors mb-10 group"
            >
              <i className="ri-arrow-left-line text-sm group-hover:-translate-x-0.5 transition-transform" />
              Back to Home
            </Link>
            <p className="text-ink/60 text-sm mb-12">
              Last updated: May 21, 2026
            </p>

            <div className="space-y-12">
              {sections.map((s) => (
                <div key={s.title}>
                  <h3 className="text-display text-xl md:text-2xl mb-4">{s.title}</h3>
                  <div className="space-y-3">
                    {s.content.map((item, i) =>
                      item.type === "text" ? (
                        <p key={i} className="text-ink/70 text-sm md:text-base leading-relaxed whitespace-pre-line">
                          {item.content}
                        </p>
                      ) : (
                        <ul key={i} className="space-y-1.5 pl-1">
                          {item.items.map((bullet, j) => (
                            <li key={j} className="flex items-start gap-2.5 text-ink/70 text-sm md:text-base leading-relaxed">
                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ink/40 shrink-0" />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
